import { useState } from "react";
import { Button } from "@project/components/ui/button";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import type { DonationStatusAction } from "@project/types/item-donation";

type ActionTarget = {
  id: number;
  label: string;
  kind: DonationStatusAction;
};

function canReviewDonationRequest(status?: string | null) {
  const value = (status ?? "").toLowerCase();
  return value !== "approved" && value !== "rejected";
}

export function DonationRequestStatusActions({
  row,
  label,
  onUpdateStatus,
  onComplete,
}: {
  row: { id: number; status?: string | null };
  label: string;
  onUpdateStatus: (
    id: number,
    status: DonationStatusAction,
    remarks: string
  ) => Promise<unknown>;
  onComplete: () => void;
}) {
  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [remarks, setRemarks] = useState("");
  const [acting, setActing] = useState(false);

  const canReview = canReviewDonationRequest(row.status);

  const openAction = (kind: DonationStatusAction) => {
    if (!canReview) {
      return;
    }
    setRemarks("");
    setTarget({ id: row.id, label, kind });
  };

  const confirm = async () => {
    if (!target) {
      return;
    }
    const trimmed = remarks.trim();
    if (!trimmed) {
      projectToast.error("Remarks are required.");
      return;
    }
    setActing(true);
    try {
      await onUpdateStatus(target.id, target.kind, trimmed);
      projectToast.success(
        target.kind === "approved"
          ? "Request approved successfully."
          : "Request rejected."
      );
      setTarget(null);
      onComplete();
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Could not update request status.")
      );
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={!canReview}
        className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
        title={canReview ? "Approve" : "Already reviewed"}
        aria-label={`Approve ${label}`}
        onClick={() => openAction("approved")}
      >
        <CheckCircle2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={!canReview}
        className="text-rose-600 hover:text-rose-700 disabled:opacity-40"
        title={canReview ? "Reject" : "Already reviewed"}
        aria-label={`Reject ${label}`}
        onClick={() => openAction("rejected")}
      >
        <XCircle className="h-4 w-4" />
      </Button>

      <Dialog
        open={!!target}
        onOpenChange={open => {
          if (!open && !acting) {
            setTarget(null);
          }
        }}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {target?.kind === "approved"
                ? "Approve donation request?"
                : "Reject donation request?"}
            </DialogTitle>
          </DialogHeader>

          {target && (
            <div className="space-y-4">
              <div className="bg-muted/40 rounded-xl border border-border px-4 py-3 text-sm">
                <p className="font-medium text-foreground">{target.label}</p>
                <p className="mt-1 text-muted-foreground">
                  {target.kind === "approved"
                    ? "This will mark the request as approved."
                    : "This will mark the request as rejected."}
                </p>
              </div>

              <div>
                <Label>Remarks *</Label>
                <Textarea
                  rows={3}
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  placeholder={
                    target.kind === "approved"
                      ? "Item received and verified."
                      : "Reason for rejection..."
                  }
                  className="mt-1.5"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={acting}
              onClick={() => setTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={acting}
              className={`rounded-xl ${
                target?.kind === "approved"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-rose-600 hover:bg-rose-700"
              }`}
              onClick={() => void confirm()}
            >
              {acting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : target?.kind === "approved" ? (
                "Approve"
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
